import React from 'react';
import styled from 'styled-components';
import QuickActions from 'components/QuickActions';
import ComponentDemo from '../../components/ComponentDemo';
import CodeBlock from '../../components/CodeBlock';
import Card from 'components/Card';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

// Icon for demo
const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const ExportIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

const QuickActionsDocs = () => {
  const exampleActions = [
    {
      label: 'Send Message',
      icon: <SendIcon />,
      onClick: () => alert('Send message clicked'),
    },
    {
      label: 'Export Data',
      icon: <ExportIcon />,
      onClick: () => alert('Export clicked'),
    },
    {
      label: 'Apply Filters',
      icon: <FilterIcon />,
      onClick: () => alert('Filter clicked'),
    },
  ];

  return (
    <div>
      <PageHeader>
        <Title>Quick Actions</Title>
        <Subtitle>
          A composite component that displays a list of action buttons in a Card.
          Commonly used in sidebars for providing quick access to common operations.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Component Overview</SectionTitle>
        <SectionDescription>
          QuickActions combines a Card component with a list of ButtonV2 components to create
          a cohesive action menu. It's used extensively in dashboard sidebars.
        </SectionDescription>

        <Card title="Features" headerBorder>
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            • <strong>Card Integration:</strong> Built on the Card component with automatic styling
            <br />
            • <strong>Button List:</strong> Automatically renders a list of action buttons
            <br />
            • <strong>Compact Layout:</strong> Uses compact Card styling for efficient space usage
            <br />
            • <strong>Icon Support:</strong> Each action can have an icon
            <br />
            • <strong>Disabled States:</strong> Individual actions can be disabled
            <br />
            • <strong>Accent Option:</strong> Optional accent title styling
            <br />• <strong>Theme Support:</strong> Full light and dark theme compatibility
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <ComponentDemo label="Quick Actions Example" align="stretch">
          <div style={{ width: '350px', maxWidth: '100%' }}>
            <QuickActions
              title="Quick Actions"
              actions={exampleActions}
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
{`import QuickActions from 'components/QuickActions';

const actions = [
  {
    label: 'Send Message',
    icon: <SendIcon />,
    onClick: handleSendMessage,
  },
  {
    label: 'Export Data',
    icon: <ExportIcon />,
    onClick: handleExport,
  },
  {
    label: 'Apply Filters',
    icon: <FilterIcon />,
    onClick: handleFilter,
  },
];

<QuickActions
  title="Quick Actions"
  actions={actions}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>With Accent Title</SectionTitle>
        <ComponentDemo label="Quick Actions with Accent" align="stretch">
          <div style={{ width: '350px', maxWidth: '100%' }}>
            <QuickActions
              title="Quick Actions"
              accentTitle
              actions={exampleActions}
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
{`<QuickActions
  title="Quick Actions"
  accentTitle
  actions={actions}
/>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>With Disabled Actions</SectionTitle>
        <ComponentDemo label="Some Actions Disabled" align="stretch">
          <div style={{ width: '350px', maxWidth: '100%' }}>
            <QuickActions
              title="Quick Actions"
              actions={[
                {
                  label: 'Send Message',
                  icon: <SendIcon />,
                  onClick: () => {},
                },
                {
                  label: 'Export Data',
                  icon: <ExportIcon />,
                  onClick: () => {},
                  disabled: true,
                },
                {
                  label: 'Apply Filters',
                  icon: <FilterIcon />,
                  onClick: () => {},
                },
              ]}
            />
          </div>
        </ComponentDemo>

        <CodeBlock>
{`const actions = [
  {
    label: 'Send Message',
    icon: <SendIcon />,
    onClick: handleSend,
  },
  {
    label: 'Export Data',
    icon: <ExportIcon />,
    onClick: handleExport,
    disabled: !hasData, // Conditionally disabled
  },
  {
    label: 'Apply Filters',
    icon: <FilterIcon />,
    onClick: handleFilter,
  },
];

<QuickActions title="Quick Actions" actions={actions} />`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Props</SectionTitle>
        <Card headerBorder title="QuickActions Props">
          <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
            • <strong>title</strong> (string): Card title (default: "Quick Actions")
            <br />
            • <strong>accentTitle</strong> (boolean): Whether to show accent bar next to title (default: false)
            <br />
            • <strong>actions</strong> (array, required): Array of action objects
            <br />
            <br />
            <strong>Action Object Props:</strong>
            <br />
            • <strong>label</strong> (string, required): Button text
            <br />
            • <strong>onClick</strong> (function): Click handler
            <br />
            • <strong>icon</strong> (node): Icon element to display
            <br />
            • <strong>disabled</strong> (boolean): Whether action is disabled
          </div>
        </Card>
      </Section>

      <Section>
        <SectionTitle>Real-World Example</SectionTitle>
        <SectionDescription>
          Example from the Customers page showing QuickActions in a sidebar layout.
        </SectionDescription>

        <CodeBlock>
{`// From Customers page
import QuickActions from 'components/QuickActions';
import { useHistory } from 'react-router-dom';

const CustomersSidebar = () => {
  const history = useHistory();

  const quickActions = [
    {
      label: 'Send Campaign',
      icon: <SendIcon />,
      onClick: () => setShowMessageComposer(true),
    },
    {
      label: 'Export Customers',
      icon: <ExportIcon />,
      onClick: handleExportCustomers,
      disabled: selectedCustomers.length === 0,
    },
    {
      label: 'Advanced Filters',
      icon: <FilterIcon />,
      onClick: () => setShowAdvancedFilters(true),
    },
    {
      label: 'View Analytics',
      icon: <ChartIcon />,
      onClick: () => history.push('/analytics'),
    },
  ];

  return (
    <aside style={{ width: '350px' }}>
      <QuickActions
        title="Quick Actions"
        accentTitle
        actions={quickActions}
      />

      {/* Other sidebar content */}
    </aside>
  );
};`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Layout Pattern</SectionTitle>
        <SectionDescription>
          QuickActions is typically used in a sidebar layout alongside main content.
        </SectionDescription>

        <CodeBlock>
{`// Dashboard layout with QuickActions sidebar
const DashboardLayout = styled.div\`
  display: flex;
  gap: 30px;

  @media (max-width: 1200px) {
    flex-direction: column;
  }
\`;

const MainContent = styled.div\`
  flex: 1;
\`;

const Sidebar = styled.aside\`
  width: 350px;

  @media (max-width: 1200px) {
    width: 100%;
  }
\`;

// Usage
<DashboardLayout>
  <MainContent>
    {/* Tables, charts, etc */}
  </MainContent>

  <Sidebar>
    <QuickActions
      title="Quick Actions"
      accentTitle
      actions={actions}
    />

    {/* Other sidebar widgets */}
  </Sidebar>
</DashboardLayout>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Best Practices</SectionTitle>
        <ComponentDemo direction="column" gap="16px">
          <Card title="✅ Do" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Keep action labels short and clear
              <br />
              • Use icons for better visual recognition
              <br />
              • Disable actions when they're not available
              <br />
              • Limit to 3-6 actions for better UX
              <br />
              • Order actions by frequency of use
              <br />• Use accentTitle to highlight important action groups
            </div>
          </Card>

          <Card title="❌ Don't" accentTitle>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              • Don't include too many actions (overwhelming)
              <br />
              • Don't use without icons (harder to scan)
              <br />
              • Don't forget to handle disabled states
              <br />
              • Don't use for navigation (use links instead)
              <br />• Don't put destructive actions without confirmation
            </div>
          </Card>
        </ComponentDemo>
      </Section>
    </div>
  );
};

export default QuickActionsDocs;